import React from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  Divider,
  IconButton,
  alpha,
} from "@mui/material";
import {
  LocalOffer,
  ShoppingCart,
  Close,
  Restaurant,
} from "@mui/icons-material";
import { formatCurrency } from "../../../utils/currencyUtils";

const OfferDetailsDialog = ({
  open,
  onClose,
  offer,
  imageUrls,
  onAddToCart,
}) => {
  if (!offer) return null;

  const calculateDiscountedPrice = (item) => {
    const price = parseFloat(item.price || 0);
    const discount = parseFloat(item.discount || 0);
    return discount > 0 ? price - (price * discount) / 100 : price;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          boxShadow: `0 24px 48px ${alpha("#000000", 0.15)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 3,
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${alpha("#f59e0b", 0.3)}`,
            }}
          >
            <LocalOffer sx={{ fontSize: 26, color: "white" }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#374151" }}>
              {offer.name}
            </Typography>
            <Chip
              label={offer.type?.toUpperCase() || "OFFER"}
              size="small"
              sx={{
                bgcolor: alpha("#f59e0b", 0.1),
                color: "#f59e0b",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: 24,
                mt: 0.5,
              }}
            />
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            bgcolor: alpha("#000000", 0.05),
            "&:hover": { bgcolor: alpha("#000000", 0.1) },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Offer Image */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                paddingTop: "100%",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: `0 8px 24px ${alpha("#000000", 0.1)}`,
              }}
            >
              {parseFloat(offer.discount || 0) > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    bgcolor: "#dc2626",
                    color: "white",
                    borderRadius: "12px",
                    px: 2,
                    py: 0.75,
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    boxShadow: `0 4px 12px ${alpha("#dc2626", 0.4)}`,
                    zIndex: 2,
                  }}
                >
                  {offer.discount}% OFF
                </Box>
              )}
              <Box
                component="img"
                src={
                  imageUrls[offer.id] ||
                  "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&h=600&fit=crop"
                }
                alt={offer.name}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Grid>

          {/* Offer Details */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#6b7280",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {offer.description || "No description available"}
              </Typography>

              {/* Price Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha("#f59e0b", 0.08),
                  borderRadius: "12px",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#f59e0b",
                  }}
                >
                  {formatCurrency(calculateDiscountedPrice(offer), "Rs", { space: true })}
                </Typography>
                {parseFloat(offer.discount || 0) > 0 && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#9ca3af",
                      textDecoration: "line-through",
                    }}
                  >
                    {formatCurrency(offer.price, "Rs", { space: true })}
                  </Typography>
                )}
              </Box>

              {/* Offer Dates */}
              {(offer.startDate || offer.endDate) && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#6b7280",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Validity Period
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#374151", mt: 0.5 }}
                  >
                    {offer.startDate &&
                      new Date(offer.startDate).toLocaleDateString()}{" "}
                    {offer.startDate && offer.endDate ? "to" : ""}{" "}
                    {offer.endDate &&
                      new Date(offer.endDate).toLocaleDateString()}
                    {!offer.startDate && !offer.endDate && "Limited time offer"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Combo Items Section */}
            {offer.OfferItems && offer.OfferItems.length > 0 && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Restaurant sx={{ fontSize: 20, color: "#f59e0b" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#374151",
                    }}
                  >
                    Includes {offer.OfferItems.length} Item
                    {offer.OfferItems.length > 1 ? "s" : ""}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {offer.OfferItems.map((offerItem) => {
                    const menuItem = offerItem.MenuItem;
                    if (!menuItem) return null;

                    return (
                      <Box
                        key={offerItem.id}
                        sx={{
                          display: "flex",
                          gap: 2,
                          p: 2,
                          bgcolor: "#f9fafb",
                          borderRadius: "12px",
                          border: `1px solid ${alpha("#000000", 0.06)}`,
                        }}
                      >
                        {/* Item Image */}
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: "10px",
                            overflow: "hidden",
                            flexShrink: 0,
                            boxShadow: `0 2px 8px ${alpha("#000000", 0.1)}`,
                          }}
                        >
                          <Box
                            component="img"
                            src={
                              imageUrls[menuItem.id] ||
                              "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=100&h=100&fit=crop"
                            }
                            alt={menuItem.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>

                        {/* Item Details */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: "#374151",
                              mb: 0.5,
                            }}
                          >
                            {menuItem.name}
                            {offerItem.quantity > 1 && (
                              <Chip
                                label={`×${offerItem.quantity}`}
                                size="small"
                                sx={{
                                  ml: 1,
                                  height: 20,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  bgcolor: alpha("#f59e0b", 0.1),
                                  color: "#f59e0b",
                                }}
                              />
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#9ca3af",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            {menuItem.description || "No description"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            {menuItem.isVegetarian && (
                              <Chip
                                label="Vegetarian"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.65rem",
                                  bgcolor: alpha("#047857", 0.1),
                                  color: "#047857",
                                }}
                              />
                            )}
                            {menuItem.isVegan && (
                              <Chip
                                label="Vegan"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.65rem",
                                  bgcolor: alpha("#0891b2", 0.1),
                                  color: "#0891b2",
                                }}
                              />
                            )}
                            {menuItem.isSpicy && (
                              <Chip
                                label="Spicy"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.65rem",
                                  bgcolor: alpha("#d97706", 0.1),
                                  color: "#d97706",
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Item Price */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            {formatCurrency(menuItem.basePrice, "Rs", { space: true })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#6b7280",
            "&:hover": {
              bgcolor: alpha("#000000", 0.05),
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<ShoppingCart />}
          onClick={() => {
            onAddToCart(offer);
            onClose();
          }}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            borderRadius: "12px",
            background: `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`,
            boxShadow: `0 4px 12px ${alpha("#f59e0b", 0.3)}`,
            "&:hover": {
              background: `linear-gradient(135deg, #d97706 0%, #b45309 100%)`,
              boxShadow: `0 6px 16px ${alpha("#f59e0b", 0.4)}`,
            },
          }}
        >
          Add to Cart
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferDetailsDialog;
